import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import api from '../services/api';
import { Book, Calendar, Folder, List as ListIcon } from 'lucide-react';
import { FileText, Search, ChevronRight, Eye, ArrowLeft, Settings, User, LayoutGrid } from '../components/icons';

export default function BaseConocimiento() {
    const { user } = useAuth();
    const { alertSuccess, alertError } = useNotificationContext();

    const [topics, setTopics] = useState([]);
    const [articulos, setArticulos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTopics, setLoadingTopics] = useState(true);
    const [topicSeleccionado, setTopicSeleccionado] = useState(null);
    const [filtros, setFiltros] = useState({
        categoria: '',
        busqueda: '',
        esPublico: null
    });
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]); // ya no se usa en UI
    const [viewMode, setViewMode] = useState('grid');
    const [paginacion, setPaginacion] = useState({
        page: 1,
        pageSize: 10,
        totalPages: 0,
        totalCount: 0
    });
    // Modal eliminado: navegación a detalle

    useEffect(() => {
        cargarTopics();
        cargarCategorias();
    }, []);

    useEffect(() => {
        if (topicSeleccionado) {
            cargarArticulos();
        }
    }, [topicSeleccionado, filtros, paginacion.page]);

    const cargarTopics = async () => {
        setLoadingTopics(true);
        try {
            const response = await api.get('/baseconocimiento/topics');
            setTopics(response.data);
        } catch (err) {
            console.error('Error cargando topics:', err);
            alertError('Error al cargar los topics');
        } finally {
            setLoadingTopics(false);
        }
    };

    const cargarArticulos = async () => {
        if (!topicSeleccionado) return;

        setLoading(true);
        try {
            // Filtrar parámetros vacíos
            const paramsFiltrados = {
                topicId: topicSeleccionado.id,
                page: paginacion.page,
                pageSize: paginacion.pageSize
            };

            // Solo agregar filtros que no estén vacíos
            if (filtros.categoria) paramsFiltrados.categoria = filtros.categoria;
            // subcategoria y nivel eliminados de filtros de UI
            if (filtros.busqueda) paramsFiltrados.busqueda = filtros.busqueda;
            if (filtros.esPublico !== null) paramsFiltrados.esPublico = filtros.esPublico;

            const params = new URLSearchParams(paramsFiltrados);
            const response = await api.get(`/baseconocimiento?${params}`);
            setArticulos(response.data.articulos);
            setPaginacion(prev => ({
                ...prev,
                totalPages: response.data.totalPages,
                totalCount: response.data.totalCount
            }));
        } catch (err) {
            console.error('Error cargando artículos:', err);
            alertError('Error al cargar los artículos de la base de conocimiento');
        } finally {
            setLoading(false);
        }
    };

    const cargarCategorias = async () => {
        try {
            const response = await api.get('/baseconocimiento/categorias');
            setCategorias(response.data);
        } catch (err) {
            console.error('Error cargando categorías:', err);
        }
    };

    const cargarSubcategorias = async () => { /* sin uso, mantenido por compatibilidad */ };

    const handleFiltroChange = (campo, valor) => {
        setFiltros(prev => ({ ...prev, [campo]: valor }));

        if (campo === 'categoria') {
            setFiltros(prev => ({ ...prev, subcategoria: '' }));
            cargarSubcategorias(valor);
        }

        setPaginacion(prev => ({ ...prev, page: 1 }));
    };

    const seleccionarTopic = (topic) => {
        setTopicSeleccionado(topic);
        setFiltros({ categoria: '', busqueda: '', esPublico: null });
        setPaginacion(prev => ({ ...prev, page: 1 }));
    };

    const navigate = useNavigate();

    const verArticulo = (id) => {
        navigate(`/base-conocimiento/articulo/${id}`);
    };


    const getNivelColor = () => 'bg-gray-100 text-gray-800';
    const getPrioridadColor = () => 'bg-gray-100 text-gray-800';

    // Acceso abierto a cualquier usuario autenticado; el backend filtra visibilidad

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Base de Conocimiento
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300">
                        Instructivos y documentación del área de TI organizados por temas
                    </p>
                </div>
                {(user?.role === 'admin' || user?.role === 'soporte' || user?.role === 'sap') && (
                    <button
                        onClick={() => navigate('/admin-base-conocimiento')}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gray-700 hover:bg-gray-800 transition-colors shadow-sm"
                    >
                        <Settings className="w-4 h-4 mr-2" />
                        Administrar
                    </button>
                )}
            </div>

            {!topicSeleccionado ? (
                /* Lista de Topics */
                loadingTopics ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        <p className="ml-3 text-gray-600 dark:text-gray-300">Cargando temas...</p>
                    </div>
                ) : topics.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-12 text-center flex flex-col items-center">
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-full mb-4">
                            <Book className="w-12 h-12 text-gray-300 dark:text-gray-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No hay temas disponibles</h3>
                        <p className="text-gray-500 dark:text-gray-400">Aún no se han creado temas en la base de conocimiento.</p>
                        {(user?.role === 'admin' || user?.role === 'soporte' || user?.role === 'sap') && (
                            <button
                                onClick={() => navigate('/admin-base-conocimiento')}
                                className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                            >
                                Crear primer tema
                            </button>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Temas</h2>
                            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    title="Vista de lista"
                                >
                                    <ListIcon className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    title="Vista de cuadrícula"
                                >
                                    <LayoutGrid className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" : "flex flex-col gap-4"}>
                            {topics.map(topic => (
                                <div
                                    key={topic.id}
                                    onClick={() => seleccionarTopic(topic)}
                                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500 transition-all cursor-pointer flex flex-col h-full overflow-hidden group"
                                >
                                    <div className={`p-5 flex flex-grow ${viewMode === 'list' ? 'flex-row items-center justify-between gap-6' : 'flex-col'}`}>
                                        <div className={`flex items-center ${viewMode === 'list' ? 'w-1/3 min-w-[200px]' : 'mb-4'}`}>
                                            <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 text-2xl mr-4 border border-blue-100 dark:border-blue-800/50 flex-shrink-0">
                                                {topic.icono || '📚'}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                    {topic.nombre}
                                                </h3>
                                                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
                                                    <FileText className="w-3.5 h-3.5 mr-1" />
                                                    {topic.cantidadArticulos} artículos
                                                </div>
                                            </div>
                                        </div>
                                        <p className={`text-sm text-gray-600 dark:text-gray-300 line-clamp-3 ${viewMode === 'list' ? 'flex-grow border-l border-gray-100 dark:border-gray-700 pl-6' : 'mb-4'}`}>
                                            {topic.descripcion}
                                        </p>
                                        <div className={`${viewMode === 'list' ? 'ml-6' : 'mt-auto pt-4 border-t border-gray-100 dark:border-gray-700/50 flex justify-between items-center'}`}>
                                            <ChevronRight className={`w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors ${viewMode === 'list' ? '' : 'ml-auto'}`} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            ) : (
                /* Vista de Artículos del Topic seleccionado */
                <>
                    {/* Breadcrumb */}
                    <div className="mb-4">
                        <nav className="flex" aria-label="Breadcrumb">
                            <ol className="inline-flex items-center space-x-2">
                                <li className="inline-flex items-center">
                                    <button
                                        onClick={() => setTopicSeleccionado(null)}
                                        className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                                    >
                                        <Book className="w-4 h-4 mr-1.5" />
                                        Base de Conocimiento
                                    </button>
                                </li>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                <li>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {topicSeleccionado.nombre}
                                    </span>
                                </li>
                            </ol>
                        </nav>
                    </div>

                    {/* Header del Topic */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center">
                                <div className="w-14 h-14 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 text-3xl mr-5 border border-blue-100 dark:border-blue-800/50 flex-shrink-0">
                                    {topicSeleccionado.icono || '📚'}
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                        {topicSeleccionado.nombre}
                                    </h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {topicSeleccionado.descripcion}
                                    </p>
                                </div>
                            </div>
                            {user?.role && (user.role === 'admin' || user.role === 'soporte' || user.role === 'sap') && (
                                <button
                                    onClick={() => navigate(`/base-conocimiento/nuevo?topicId=${encodeURIComponent(topicSeleccionado.id)}`)}
                                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm flex-shrink-0"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Nuevo Artículo
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filtros */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-4 mt-6">
                        <div className="flex-1">
                            <div className="relative">
                                <div className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
                                    <Search className="w-5 h-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={filtros.busqueda}
                                    onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                                    placeholder="Buscar en artículos..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                                />
                            </div>
                        </div>
                        <div className="w-full sm:w-64">
                            <div className="relative">
                                <Folder className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <select
                                    value={filtros.categoria}
                                    onChange={(e) => handleFiltroChange('categoria', e.target.value)}
                                    className="w-full pl-9 pr-8 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white appearance-none shadow-sm cursor-pointer"
                                >
                                    <option value="">Todas las categorías</option>
                                    {categorias.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Lista de artículos (Cards stack) */}
                    <div>
                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 animate-pulse">
                                        <div className="flex gap-4">
                                            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0"></div>
                                            <div className="flex-1 space-y-3">
                                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4">
                                    {articulos.length === 0 ? (
                                        <div className="bg-white dark:bg-gray-800 p-10 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400">
                                            <Search className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                                            No se encontraron artículos que coincidan con los filtros.
                                        </div>
                                    ) : (
                                        articulos.map(articulo => (
                                            <div
                                                key={articulo.id}
                                                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500 transition-all cursor-pointer group"
                                                onClick={() => verArticulo(articulo.id)}
                                            >
                                                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                                                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 transition-colors">
                                                        <FileText className="w-6 h-6" />
                                                    </div>

                                                    <div className="flex-1">
                                                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                                                            <h4 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                                {articulo.titulo}
                                                            </h4>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <span className="px-2.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 flex items-center">
                                                                    <Folder className="w-3 h-3 mr-1" />
                                                                    {articulo.categoria}
                                                                </span>
                                                                {articulo.esPublico && (
                                                                    <span className="px-2.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:border-green-800/50 dark:text-green-400">
                                                                        Público
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                                                            {articulo.descripcion}
                                                        </p>

                                                        <div className="flex items-center text-[11px] font-semibold text-gray-500 dark:text-gray-400 gap-4 mt-auto">
                                                            <div className="flex items-center">
                                                                <Eye className="w-3.5 h-3.5 mr-1 text-gray-400" />
                                                                {articulo.visitas} vistas
                                                            </div>
                                                            <div className="flex items-center">
                                                                <Calendar className="w-3.5 h-3.5 mr-1 text-gray-400" />
                                                                {new Date(articulo.fechaCreacion).toLocaleDateString()}
                                                            </div>
                                                            {articulo.autor && (
                                                                <div className="flex items-center">
                                                                    <User className="w-3.5 h-3.5 mr-1 text-gray-400" />
                                                                    {articulo.autor}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Paginación */}
                                {paginacion.totalPages > 1 && (
                                    <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                                        <div className="flex justify-center space-x-2">
                                            <button
                                                onClick={() => setPaginacion(prev => ({ ...prev, page: prev.page - 1 }))}
                                                disabled={paginacion.page === 1}
                                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                                            >
                                                Anterior
                                            </button>

                                            <span className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                                Página {paginacion.page} de {paginacion.totalPages}
                                            </span>

                                            <button
                                                onClick={() => setPaginacion(prev => ({ ...prev, page: prev.page + 1 }))}
                                                disabled={paginacion.page === paginacion.totalPages}
                                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                                            >
                                                Siguiente
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )
            }

        </div >
    );
}
